import { t } from 'i18next';
import { Col } from '../../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../../shared/frontend/components/grid/Row';
import { processCharacterSpecialItems } from '../../../../../../../shared/frontend/scripts/wow/gearHelpers';
import { WowheadItemLink, WowheadLink } from '../../../../../../../shared/frontend/scripts/wow/components/WowheadLink';
import WowIcon from '../../../../../../../shared/frontend/scripts/wow/components/WowIcon';
import { categorizeSpentLoadoutTalents } from '../../../../../../../shared/common/talentHelpers';
import { TalentIcon } from '../../../../../../../shared/frontend/scripts/wow/components/TalentIcon';
import { Stack } from '../../../../../../../shared/frontend/components/stack/Stack';

interface ICharacterCalloutProps {
  items?: Record<string, any> | null;
  talentLoadout?: Record<string, any> | null;
  talentsDetails?: Record<string, any>[] | null;
  interestingAuras?: Record<string, any>[] | null;
  covenant?: Record<string, any> | null;
  character: {
    classId: number;
    specId: number;
  };
}

const SLOTS = [
  'head',
  'neck',
  'shoulder',
  'back',
  'chest',
  'wrist',
  'hands',
  'waist',
  'legs',
  'feet',
  'finger1',
  'finger2',
  'trinket1',
  'trinket2',
  'mainhand',
  'offhand'
];

export const CharacterCallout = (props: ICharacterCalloutProps) => {
  const { items, character, talentLoadout, interestingAuras, talentsDetails, covenant } = props;
  const specialItems = items ? processCharacterSpecialItems(items) : null;

  let categorizedTalents: any = null;

  if (talentLoadout) {
    categorizedTalents = categorizeSpentLoadoutTalents(character.specId, talentLoadout.loadout, { showAll: true });
  }

  return (
    <Row direction="column" verticalAlign="center" gutter={'xx-small'}>
      {items && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.gear-label')}</div>
            <Stack justifyContent="center" spacingInRems={2} wrap className="rio-callout--content">
              {SLOTS.map((slot) => {
                if (!items[slot]) {
                  return;
                }

                return (
                  <WowheadItemLink
                    key={slot}
                    item={items[slot]}
                    classId={character.classId}
                    {...(items[slot].tier
                      ? {
                          setPieces: specialItems[`tier${items[slot].tier}`]
                        }
                      : {})}
                  >
                    <WowIcon
                      size="small"
                      icon={items[slot].icon}
                      imgClassName={`border-item-quality--${items[slot].item_quality}`}
                    />
                  </WowheadItemLink>
                );
              })}
            </Stack>
          </div>
        </Col>
      )}

      {covenant && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.soulbind-traits-label')}</div>
            <Stack justifyContent="center" spacingInRems={2} wrap className="rio-callout--content">
              {covenant?.soulbind?.traits?.map((trait: any) => (
                <WowheadLink key={trait.spell.id} type="spell" id={trait.spell.id}>
                  <WowIcon icon={trait.spell.icon} size="small" shape="circle" />
                </WowheadLink>
              ))}
            </Stack>
          </div>
        </Col>
      )}

      {covenant && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.soulbind-conduits-label')}</div>
            <Stack justifyContent="center" spacingInRems={2} wrap className="rio-callout--content">
              {covenant.soulbind.conduits.map((conduit: any) => (
                <WowheadLink key={conduit.spell.id} type="spell" id={conduit.spell.id}>
                  <WowIcon icon={conduit.spell.icon} size="small" shape="octagon" />
                </WowheadLink>
              ))}
            </Stack>
          </div>
        </Col>
      )}

      {!talentLoadout && talentsDetails && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.talents-label')}</div>
            <Stack justifyContent="center" spacingInRems={2} wrap className="rio-callout--content">
              {talentsDetails.map((talent: any, talentIndex: number) => (
                <WowheadLink
                  key={talentIndex}
                  type="spell"
                  id={talent.spellId}
                  className={talentIndex > 0 ? 'slds-m-left--xx-small' : ''}
                >
                  <WowIcon size="small" icon={talent.icon} />
                </WowheadLink>
              ))}
            </Stack>
          </div>
        </Col>
      )}

      {categorizedTalents && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.class-talents-label')}</div>
            <Stack
              justifyContent="center"
              spacingInRems={2}
              wrap
              className="rio-callout--content slds-text-align--center"
            >
              {categorizedTalents.classTalents.map((entry: any, talentIndex: number) => (
                <TalentIcon
                  talent={entry}
                  size="small"
                  key={talentIndex}
                  className={talentIndex > 0 ? 'slds-m-left--xx-small' : ''}
                />
              ))}
            </Stack>
          </div>
        </Col>
      )}

      {categorizedTalents && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.spec-talents-label')}</div>
            <Stack
              justifyContent="center"
              spacingInRems={2}
              wrap
              className="rio-callout--content slds-text-align--center"
            >
              {categorizedTalents.specTalents.map((entry: any, talentIndex: number) => (
                <TalentIcon
                  talent={entry}
                  size="small"
                  key={talentIndex}
                  className={talentIndex > 0 ? 'slds-m-left--xx-small' : ''}
                />
              ))}
            </Stack>
          </div>
        </Col>
      )}

      {categorizedTalents && categorizedTalents.heroTalents.length > 0 && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.hero-talents-label')}</div>
            <Stack
              justifyContent="center"
              spacingInRems={2}
              wrap
              className="rio-callout--content slds-text-align--center"
            >
              {categorizedTalents.heroTalents.map((entry: any, talentIndex: number) => (
                <TalentIcon
                  talent={entry}
                  size="small"
                  key={talentIndex}
                  className={talentIndex > 0 ? 'slds-m-left--xx-small' : ''}
                />
              ))}
            </Stack>
          </div>
        </Col>
      )}

      {interestingAuras && interestingAuras.length > 0 && (
        <Col grow={false}>
          <div className="rio-callout">
            <div className="rio-callout--header">{t('mplus-runs:expanded-player.interesting-auras-label')}</div>
            <Stack
              justifyContent="center"
              spacingInRems={2}
              wrap
              className="rio-callout--content slds-text-align--center"
            >
              {interestingAuras.map((aura: any, auraIndex: number) => (
                <WowheadLink
                  key={auraIndex}
                  type="spell"
                  id={aura.id}
                  className={auraIndex > 0 ? 'slds-m-left--xx-small' : ''}
                >
                  <WowIcon size="small" icon={aura.icon} />
                </WowheadLink>
              ))}
            </Stack>
          </div>
        </Col>
      )}
    </Row>
  );
};
